import { CommonModule, DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy } from '@angular/core';
// import { NotificationService } from '../../../core/services/notification/notification.service';
import { Notification } from '../../../core/models/common.models';
import { NotificationService } from '../../../core/services/notification/notification.service';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationComponent implements AfterViewInit, OnDestroy {
  notificationService = inject(NotificationService);
  private readonly document = inject(DOCUMENT);
  private readonly hostElement = inject(ElementRef<HTMLElement>);

  private originalParent: Node | null = null;
  private nextSibling: Node | null = null;
  private overlayObserver: MutationObserver | null = null;

  ngAfterViewInit(): void {
    const host = this.hostElement.nativeElement;
    this.originalParent = host.parentNode;
    this.nextSibling = host.nextSibling;

    this.overlayObserver = new MutationObserver(() => {
      this.syncHostContainer();
    });

    this.overlayObserver.observe(this.document.body, {
      childList: true,
      subtree: true,
    });

    this.syncHostContainer();
  }

  ngOnDestroy(): void {
    this.overlayObserver?.disconnect();
    this.overlayObserver = null;
    this.restoreHostPosition();
  }

  private syncHostContainer(): void {
    const host = this.hostElement.nativeElement;
    const wrappers = this.document.querySelectorAll('.cdk-global-overlay-wrapper');
    const overlayContainer = wrappers.length > 0 ? wrappers[wrappers.length - 1] : null;

    if (!(overlayContainer instanceof HTMLElement)) {
      this.restoreHostPosition();
      return;
    }

    const hasActiveOverlay = Array.from(overlayContainer.children).some((child) => child !== host);

    if (!hasActiveOverlay) {
      this.restoreHostPosition();
      return;
    }

    if (host.parentNode !== overlayContainer || overlayContainer.lastElementChild !== host) {
      overlayContainer.appendChild(host);
    }
  }

  private restoreHostPosition(): void {
    const host = this.hostElement.nativeElement;

    if (!this.originalParent || host.parentNode === this.originalParent) {
      return;
    }

    if (this.nextSibling && this.nextSibling.parentNode === this.originalParent) {
      this.originalParent.insertBefore(host, this.nextSibling);
      return;
    }

    this.originalParent.appendChild(host);
  }

  getClass(type: Notification['type']): string {
    switch (type) {
      case 'success':
        return 'bg-green-600 dark:bg-green-600 text-green-700 dark:text-green-200 border-green-400';
      case 'error':
        return 'bg-red-600 dark:bg-red-800 text-red-700 dark:text-red-200 border-red-400';
      case 'warning':
        return 'bg-yellow-600 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200 border-yellow-400';
      case 'info':
        return 'bg-primary dark:bg-blue-800 text-blue-700 dark:text-blue-200 border-blue-400';
      default:
        return 'bg-primary dark:bg-blue-800 text-blue-700 dark:text-blue-200 border-blue-400';
    }
  }
}
